import React, { useEffect, useState } from 'react';
import {
    Box,
    CircularProgress,
    FormControl,
    FormHelperText,
    RadioGroup,
    makeStyles,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useGetLeverandorVurderingListQuery } from '../../../services/rtkApi';
import { Alert } from '@material-ui/lab';
import { LeverandorSuperofficeUrl } from '@klp/leverandor/config';
import { VurderingListItem } from './VurderingListItem';

const useStyles = makeStyles((theme) => ({
    horizontalMargin: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    marginBottom: {
        marginBottom: theme.spacing(2),
    },
}));

export interface SuperOfficeContactSelectProps {
    orgnummer: string;
    touched?: boolean;
    error?: string;
    onChange: (contactId: string | undefined) => void;
}

export const SuperOfficeContactSelect: React.FC<
    SuperOfficeContactSelectProps
> = ({ orgnummer, touched, error, onChange }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const {
        data: vurderinger,
        isLoading,
        isError,
        isSuccess,
    } = useGetLeverandorVurderingListQuery({ orgnummer }, { skip: !orgnummer });
    const [selectedContactId, setSelectedContactId] = useState<string>();

    const leverandorVurderingNotFound =
        isError || (isSuccess && vurderinger.length === 0);
    const hasDuplicates = isSuccess && vurderinger.length > 1;
    const singleLeverandorVurderingFound = isSuccess && !hasDuplicates;

    // On fetch success, select first contact
    useEffect(() => {
        const selectedVurdering = vurderinger?.find(
            (v) => v.orgnummer === orgnummer && v.superOfficeContactId,
        );

        if (selectedVurdering) {
            onChange(selectedVurdering.superOfficeContactId);
            setSelectedContactId(selectedVurdering.superOfficeContactId);
        } else {
            onChange(undefined);
            setSelectedContactId(undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vurderinger, orgnummer]);

    const handleVurderingSelection = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const contactId = event.target.value;

        setSelectedContactId(contactId);
        onChange(contactId);
    };

    const handleSuperofficeIdSelect =
        (superOfficeContactId?: string) =>
        (event: React.MouseEvent<HTMLAnchorElement>) => {
            if (!superOfficeContactId) return;
            event.preventDefault();
            window.open(
                LeverandorSuperofficeUrl(superOfficeContactId),
                '_blank',
            );
        };

    if (isLoading) {
        return (
            <Box display="flex" alignItems="center">
                <CircularProgress size={20} />
                <span>
                    {t('leverandor.createLeverandor.superOffice.loading')}
                </span>
            </Box>
        );
    }

    if (leverandorVurderingNotFound) {
        return (
            <Alert severity="info" className={classes.horizontalMargin}>
                {t('leverandor.createLeverandor.superOffice.notFoundAlert')}
                <br />
                <small>
                    {t(
                        'leverandor.createLeverandor.superOffice.notFoundAlertDescription',
                    )}
                </small>
            </Alert>
        );
    }

    if (singleLeverandorVurderingFound) {
        return (
            <Alert severity="success" className={classes.horizontalMargin}>
                {t('leverandor.createLeverandor.superOffice.foundAlert')}
                <br />
                <small>
                    {vurderinger[0]?.navn &&
                        t(
                            'leverandor.createLeverandor.superOffice.foundAlertDescription',
                            { navn: vurderinger[0].navn },
                        )}
                </small>
            </Alert>
        );
    }

    if (!selectedContactId) return null;

    // Flere Leverandørvurderinger funnet - vis valg
    return (
        <FormControl fullWidth={true} error={touched && !!error}>
            <Box>
                <Box className={classes.marginBottom}>
                    <Alert severity="warning" className={classes.marginBottom}>
                        {t(
                            'leverandor.createLeverandor.duplicateSuperofficeContactsFound',
                            {
                                count: vurderinger?.length ?? 0,
                            },
                        )}
                    </Alert>

                    <RadioGroup
                        value={selectedContactId}
                        onChange={handleVurderingSelection}
                    >
                        {vurderinger?.map((vurdering) => (
                            <VurderingListItem
                                key={vurdering.superOfficeContactId}
                                vurdering={vurdering}
                                value={vurdering.superOfficeContactId}
                                onSuperofficeIdClick={handleSuperofficeIdSelect}
                            />
                        ))}
                    </RadioGroup>
                </Box>
                {touched && error && <FormHelperText>{error}</FormHelperText>}
            </Box>
        </FormControl>
    );
};
